import _ from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import DeleteIcon from '@material-ui/icons/Delete';
import Select from '@material-ui/core/Select';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';

import StationsContainer from '../containers/Stations';
import NotificationJobContainer from '../containers/NotificationJob';
import FMPSelect from '../components/pureForm/FMPSelect';
import FMPCheckbox from '../components/pureForm/FMPCheckbox';
import { sortStationsByNumber, validateEmail } from '../libs/utility';

const styles = (theme) => {
	const primaryColor = theme.palette.primary[500];

	return {
		section: {
			margin: '50px 0px 10px 0px',
			textAlign: 'left',
		},
		sectionHeader: {
			textTransform: 'uppercase',
			fontSize: 14,
			color: '#777',
			fontWeight: 900,
			letterSpacing: 2,
		},
		sectionDivider: {
			backgroundColor: primaryColor,
		},
		paper: {
			width: '100%',
			overflowX: 'auto',
			textAlign: 'left',
		},
		innerPaper: {
			padding: 20,
			[theme.breakpoints.up('md')]: {
				padding: '10px 40px 40px 40px',
			},
		},
		addInputButton: {
			borderRadius: '100px',
			marginTop: 20,
			marginBottom: 30,
			textTransform: 'uppercase',
		},
		submitButton: {
			fontSize: 18,
			borderRadius: '100px',
			padding: '20px 30px',
			textTransform: 'uppercase',
		},
		formControl: {
			width: '100%',
			margin: '10px 0',
		},
	};
};

class AddNotificationJob extends Component {
	constructor(props) {
		super(props);

		const updateId = _.get(this.props, ['match', 'params', 'id']);

		this.state = {
			updateId: updateId ? parseInt(updateId, 10) : undefined,
			isUpdate: updateId,
			name: '',
			emails: [''],
			stationIds: [''],
			formulaType: '',
		};

		this.onChange = this.onChange.bind(this);
		this.onArrayChange = this.onArrayChange.bind(this);
		this.addInput = this.addInput.bind(this);
		this.removeInput = this.removeInput.bind(this);
	}

	componentDidMount() {
		if (!this.props.stations.length) {
			this.props.searchStations();
		}

		if (this.state.isUpdate) {
			if (!this.props.notificationJob) {
				this.props.getNotificationJob({
					params: {
						notificationJobIds: [this.state.updateId],
					},
				});
			} else {
				this.updateDefaultValues(this.props);
			}
		} else {
			this.updateDefaultValues(this.props);
		}
	}

	updateDefaultValues(props) {
		if (props.notificationJob) {
			const {
				name,
				stationIds,
				emails,
				formulaType,
				showDiffFromYesterday,
			} = props.notificationJob;
			this.setState({ name, stationIds, emails, formulaType, showDiffFromYesterday });
		}
	}

	componentDidUpdate(prevProps) {
		if (_.isUndefined(prevProps.notificationJob) && this.props.notificationJob) {
			this.updateDefaultValues(this.props);
		}
	}

	onAddUpdateJob = () => {
		const { showErrors } = this.props;

		//- Check required fields
		const formFieldError = {};
		[
			{ fieldName: 'name', display: 'Name' },
			{ fieldName: 'formulaType', display: 'Formula Type' },
		].forEach((s) => {
			if (!this.state[s.fieldName]) {
				formFieldError[s.fieldName] = `${s.display} is required`;
			}
		});
		if (_.keys(formFieldError).length) {
			this.setState({ formFieldError });
			showErrors(_.values(formFieldError));
			return;
		}

		//- Check all the stations and make sure they have the right formula
		const { stationIds, formulaType, emails } = this.state;
		if (_.isEmpty(stationIds)) {
			showErrors(['At least one station is required']);
			return;
		}

		const { stations } = this.props;
		const errors = [];
		stationIds.forEach((stationId) => {
			const station = _.find(stations, { stationId });
			if (!station) {
				errors.push(`Station not found from stationId:${stationId}`);
				return;
			}
			if (formulaType === 'biodiesel' && station.biodieselFormulaId === 0) {
				errors.push(
					`Biodiesel formula selected but station ${station.number} ` +
						`does not have a biodiesel formula set`
				);
			} else if (formulaType === 'e85' && station.gasFormulaId === 0) {
				errors.push(
					`E85 formula selected but station ${station.number} does not have a e85 formula set`
				);
			}
		});

		if (_.isEmpty(emails)) {
			errors.push('You must have at least one email');
		} else {
			emails.forEach((email, i) => {
				if (!email) {
					errors.push(`Email #${i} is empty`);
					return;
				}

				if (!validateEmail(email)) {
					errors.push(`Email #${i} (${email}) is not a valid email format`);
				}
			});
		}

		if (!_.isEmpty(errors)) {
			showErrors(errors);
			return;
		}

		//- time to submit
		const params = {};
		['name', 'formulaType', 'showDiffFromYesterday'].forEach((fieldName) => {
			params[fieldName] = this.state[fieldName];
		});
		params.emails = this.state.emails;
		params.stationIds = this.state.stationIds;

		if (this.state.isUpdate) {
			params.notificationJobId = this.state.updateId;
			this.props.updateNotificationJob({ params });
		} else {
			this.props.addNotificationJob({ params });
		}
	};

	onChange(event, fieldName) {
		this.setState({ [fieldName]: event.target.value });
	}

	onArrayChange(event, arrayName, index) {
		const inputs = this.state[arrayName].slice();
		inputs[index] = event.target.value;
		this.setState({ [arrayName]: inputs });
	}

	addInput(arrayName) {
		const inputs = this.state[arrayName];
		inputs.push('');
		this.setState({ [arrayName]: inputs });
	}

	removeInput(arrayName, index) {
		const inputs = this.state[arrayName].slice();
		inputs.splice(index, 1);
		this.setState({ [arrayName]: inputs });
	}

	handleChange = _.debounce((value, path) => {
		const arrayPath = _.toPath(path);
		const basePath = _.head(arrayPath);
		let base = this.state[basePath];
		let newState = {};

		if (_.isArray(base) || _.isObject(base)) {
			base = _.cloneDeep(base);
			_.set(base, arrayPath.slice(1), value);
			newState[basePath] = base;
		} else {
			newState[basePath] = value;
		}

		//- Clear any errors on the path
		let formFieldError = Object.assign({}, this.state.formFieldError);
		delete formFieldError[path];
		newState.formFieldError = formFieldError;

		this.setState(newState);
	});

	getInputProps = (path) => {
		return {
			path,
			value: this.getValue(path),
			error: this.getError(path),
			onChange: this.handleChange,
			checked: this.getValue(path, false),
			md: 6,
		};
	};

	getValue = (path, defaultValue) => {
		const _d = _.isUndefined(defaultValue) ? '' : defaultValue;
		const val = _.get(this.state, path, _d);
		return _.isNull(val) ? '' : val;
	};

	getError = (path) => {
		return _.get(this.state.formFieldError, path);
	};

	render() {
		const { classes, stations, notificationJob, authUser } = this.props;
		const { isUpdate } = this.state;

		if (!authUser) {
			return null;
		}

		const canViewPricingNotifications = _.get(authUser, [
			'permissions',
			'canViewPricingNotifications',
		]);
		if (!canViewPricingNotifications) {
			return <div>INVALID PERMISSIONS! GET OUTTA HERE!</div>;
		}

		const canEditPricingNotifications = _.get(authUser, [
			'permissions',
			'canEditPricingNotifications',
		]);

		if (!stations.length) {
			return <div>Fetching stations...</div>;
		}
		if (isUpdate && !notificationJob) {
			return <div>Fetching job details...</div>;
		}

		const stationOptions = sortStationsByNumber(stations).map((station) => {
			return {
				value: station.stationId,
				display: `${station.number} ${station.name}`,
			};
		});

		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<Typography variant="h5" align="left" gutterBottom>
								{isUpdate ? 'Update' : 'Add'} Notification Job
							</Typography>
						</Grid>
					</Grid>

					<Paper className={classes.paper}>
						<div className={classes.innerPaper}>
							<div className={classes.section} style={{ marginTop: 30 }}>
								<div className={classes.sectionHeader}>Job Info</div>
							</div>
							<Grid item xs={12} md={6}>
								<TextField
									className={classes.formControl}
									value={this.state.name}
									onChange={(event) => this.onChange(event, 'name')}
									label="Job name"
									fullWidth
								/>
							</Grid>
							<Grid item xs={12} md={6}></Grid>
							<FMPCheckbox
								{...this.getInputProps('showDiffFromYesterday')}
								label={'Include difference from yesterday in pricing'}
								value={'showDiffFromYesterday'}
							/>
							<div className={classes.section} style={{ marginTop: 30 }}>
								<div className={classes.sectionHeader}>Stations</div>
							</div>
							{this.state.stationIds.map((stationId, i) => {
								return (
									<Grid key={i} container spacing={3}>
										<Grid item xs={11} md={5}>
											<FormControl className={classes.formControl}>
												<InputLabel htmlFor={`station${i}`}>Station {i + 1}</InputLabel>
												<Select
													value={stationId}
													onChange={(event) => this.onArrayChange(event, 'stationIds', i)}
													input={<Input id={`station${i}`} />}
												>
													{stationOptions.map((option, i) => {
														return (
															<MenuItem key={i} value={option.value}>
																{option.display}
															</MenuItem>
														);
													})}
												</Select>
											</FormControl>
										</Grid>
										<Grid item xs={1}>
											{this.state.stationIds.length >= 2 && (
												<IconButton
													color="primary"
													className={classes.button}
													onClick={() => this.removeInput('stationIds', i)}
												>
													<DeleteIcon />
												</IconButton>
											)}
										</Grid>
									</Grid>
								);
							})}
							<Button
								variant="contained"
								className={classes.addInputButton}
								onClick={() => this.addInput('stationIds')}
							>
								Add another station
							</Button>
							<FMPSelect
								label={'Formula type to use for pricing'}
								{...this.getInputProps('formulaType')}
								options={[
									{ value: 'e85', display: 'E85 Pricing Formula' },
									{ value: 'biodiesel', display: 'Biodiesel Pricing Formula' },
								]}
								md={5}
							/>
							<div className={classes.section} style={{ marginTop: 30 }}>
								<div className={classes.sectionHeader}>Emails</div>
							</div>
							{this.state.emails.map((email, i) => {
								return (
									<Grid key={i} container spacing={3}>
										<Grid item xs={11} md={5}>
											<TextField
												className={classes.formControl}
												value={email}
												onChange={(event) => this.onArrayChange(event, 'emails', i)}
												label={`Email ${i + 1}`}
												fullWidth
											/>
										</Grid>
										<Grid item xs={1}>
											{this.state.emails.length >= 2 && (
												<IconButton
													color="primary"
													className={classes.button}
													onClick={() => this.removeInput('emails', i)}
												>
													<DeleteIcon />
												</IconButton>
											)}
										</Grid>
									</Grid>
								);
							})}
							<Button
								variant="contained"
								className={classes.addInputButton}
								style={{ marginTop: 20 }}
								onClick={() => this.addInput('emails')}
							>
								ADD ANOTHER EMAIL
							</Button>
							<div style={{ textAlign: 'right' }}>
								<Button
									variant="contained"
									color="primary"
									className={classes.submitButton}
									onClick={this.onAddUpdateJob}
									disabled={!canEditPricingNotifications}
								>
									{isUpdate ? 'Update' : 'Create'} Notification Job
								</Button>
							</div>
						</div>
					</Paper>
				</Grid>
			</Grid>
		);
	}
}

AddNotificationJob.propTypes = {
	classes: PropTypes.object.isRequired,
	searchStations: PropTypes.func.isRequired,
	stations: PropTypes.array,
	notificationJob: PropTypes.object,
	addNotificationJob: PropTypes.func.isRequired,
	updateNotificationJob: PropTypes.func.isRequired,
	getNotificationJob: PropTypes.func,
	showErrors: PropTypes.func,
	authUser: PropTypes.object,
};

export default _.flow([withStyles(styles), NotificationJobContainer, StationsContainer])(
	AddNotificationJob
);
